import { Component, ChangeDetectionStrategy, signal, inject, PLATFORM_ID, OnInit, OnDestroy, computed, output } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { EssayEditorComponent, DRAFT_STORAGE_KEY, Banca } from '../essay-editor/essay-editor.component';
import { CorrectionReportComponent } from '../correction-report/correction-report.component';
import { AiService } from '../../services/ai.service';
import {
  Correction,
  CorrectionLayer,
  EvolutionPlan,
  CorrectionReportData,
  isFullCorrection,
  CorrectionWithUser,
} from '../../models/correction.model';
import { EvolutionTrackerComponent } from '../evolution-tracker/evolution-tracker.component';
import { EvolutionPlanComponent } from '../evolution-plan/evolution-plan.component';
import { AuthService } from '../../services/auth.service';
import { LogService } from '../../services/log.service';
import { SupabaseService } from '../../services/supabase.service';
import { UserPlan } from '../../models/user.model';

const PREFERRED_LAYERS_KEY = 'correctify-preferred-layers';

type View = 'editor' | 'report' | 'evolutionPlan';
type SubscriptionSummary = {
  status: 'active' | 'canceled' | 'expired' | 'pending' | 'none';
  planName: string;
  endDate: string | null;
};

@Component({
  selector: 'app-dashboard',
  standalone: true,
  template: `
<div class="container mx-auto px-4 py-8 md:py-10">
  <div class="mx-auto max-w-7xl space-y-6">
    <section class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900 p-6 text-white shadow-elevated md:p-8">
      <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(59,130,246,.15),transparent_50%)]" aria-hidden="true"></div>
      <div class="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-300/90">Painel do aluno</p>
          <h1 class="mt-2 text-2xl font-bold tracking-tight md:text-3xl">
            @if (currentUser(); as user) {
              {{ user.name.split(' ')[0] }}, vamos evoluir hoje?
            } @else {
              Seu painel
            }
          </h1>
          <p class="mt-1.5 text-sm text-slate-300">Perfil, assinatura, créditos e desempenho em uma visão unificada.</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button (click)="view.set('editor')" class="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur-sm transition hover:bg-white/20">Nova correção</button>
          <button (click)="goToLatestReport()" [disabled]="!correctionResult()" class="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur-sm transition disabled:opacity-40 hover:bg-white/20">Último relatório</button>
          <button (click)="handleGeneratePlanRequest()" class="rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-900 transition hover:bg-blue-50">Plano de evolução</button>
        </div>
      </div>
    </section>

    <section class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <article class="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Plano</p>
        <p class="mt-2 text-lg font-bold tracking-tight text-slate-900">{{ getPlanLabel(userPlan()) }}</p>
        <p class="mt-0.5 text-sm text-slate-500">{{ subscriptionSummary().planName }}</p>
      </article>
      <article class="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Créditos</p>
        <p class="mt-2 text-lg font-bold tracking-tight text-slate-900">
          @if (credits() > 900) { &infin; } @else { {{ credits() }} }
        </p>
        <p class="mt-0.5 text-sm text-slate-500">Saldo para novas correções</p>
      </article>
      <article class="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Assinatura</p>
        <p class="mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-bold" [class]="getSubscriptionBadgeClass(subscriptionSummary().status)">
          {{ getSubscriptionStatusLabel(subscriptionSummary().status) }}
        </p>
        <p class="mt-2 text-sm font-semibold text-slate-900">{{ getSubscriptionEndDateLabel(subscriptionSummary().endDate) }}</p>
      </article>
    </section>

    <section class="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <article class="rounded-2xl border border-blue-100 bg-gradient-to-br from-white to-blue-50/60 p-5 shadow-card">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-blue-600">Meta de nota</p>
        <p class="mt-2 text-2xl font-extrabold tracking-tight text-slate-900">
          {{ getMentorshipCurrentNoteLabel() }} <span class="text-slate-300 mx-1">&rarr;</span> {{ getMentorshipTargetNote() }}
        </p>
        <p class="mt-2 text-sm text-slate-500">Prazo estimado: <span class="font-semibold text-slate-800">{{ getMentorshipTimelineLabel() }}</span></p>
      </article>

      <article class="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Diagnóstico por banca</p>
        <p class="mt-2 text-sm text-slate-500">Banca foco: <span class="font-semibold text-slate-800">{{ getMentorshipBancaFocus() }}</span></p>
        <p class="mt-1 text-sm text-slate-500">Ponto crítico: <span class="font-semibold text-slate-800">{{ getMentorshipWeakestCriterion() }}</span></p>
      </article>

      <article class="rounded-2xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/50 p-5 shadow-card">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-indigo-600">Próxima ação (15–20 min)</p>
        <p class="mt-2 text-sm leading-relaxed text-slate-600">{{ getMentorshipNextAction() }}</p>
      </article>
    </section>

    <div class="grid grid-cols-1 gap-8 lg:grid-cols-5">
      <div class="space-y-6 lg:col-span-3">
        @if (userPlan() === 'gratuito') {
          <div class="rounded-2xl border p-5" [class.border-green-200]="hasFreeCredit()" [class.bg-green-50]="hasFreeCredit()" [class.border-amber-200]="!hasFreeCredit()" [class.bg-amber-50]="!hasFreeCredit()">
            @if (freePlanHasPaidCredits()) {
              <h2 class="text-lg font-bold text-green-900">Crédito gratuito disponível</h2>
              <p class="mt-1 text-sm text-green-800">Você ainda pode enviar uma redação grátis neste mês.</p>
            } @else {
              <h2 class="text-lg font-bold text-amber-900">Crédito gratuito esgotado</h2>
              <p class="mt-1 text-sm text-amber-800">Faça upgrade para continuar com correções ilimitadas.</p>
              <button (click)="handleViewPlans()" class="mt-3 rounded-lg bg-custom-blue px-4 py-2 text-sm font-bold text-white hover:opacity-90">Ver planos</button>
            }
          </div>
        }

        <section class="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card md:p-6">
          @if (isGeneratingPlan()) {
            <div class="p-6 text-center">
              <div class="loader mx-auto"></div>
              <p class="mt-4 text-lg font-semibold text-custom-black">Gerando seu plano de evolução...</p>
              <p class="mt-2 text-sm text-gray-600">Analisando seu histórico para montar uma estratégia personalizada.</p>
            </div>
          } @else {
            @switch (view()) {
              @case ('editor') {
                <app-essay-editor
                  (essaySubmit)="handleEssaySubmission($event)"
                  [isLoading]="isLoading()"
                  [loadingStatus]="loadingStatus()"
                  [loadingSteps]="loadingSteps()"
                  [credits]="credits()"
                  [userPlan]="userPlan()"
                  [hasFreeCredit]="hasFreeCredit()">
                </app-essay-editor>
              }
              @case ('report') {
                @if (correctionResult()) {
                  @if (getCurrentCorrectionEssay()) {
                    <section class="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
                      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h3 class="text-sm font-bold uppercase tracking-wide text-slate-600">Redação enviada</h3>
                        @if (getCurrentCorrectionTheme()) {
                          <span class="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                            Tema: {{ getCurrentCorrectionTheme() }}
                          </span>
                        }
                      </div>
                      <div class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                        {{ getCurrentCorrectionEssay() }}
                      </div>
                    </section>
                  }
                  <app-correction-report
                    [report]="correctionResult()!"
                    [correctionId]="currentCorrectionId()"
                    [history]="reportHistory()"
                    (newCorrection)="startNewCorrection()"
                    [initialVisibleLayers]="preferredLayers()"
                    (visibleLayersChange)="handleVisibleLayersChange($event)"
                    (viewPlansRequest)="handleViewPlans()"
                    [showUpgradeBannerForTrialUser]="!isProTrialAvailable() && userPlan() === 'gratuito'">
                  </app-correction-report>
                } @else {
                  <div class="p-8 text-center text-slate-500">Nenhum relatório aberto. Envie uma redação para começar.</div>
                }
              }
              @case ('evolutionPlan') {
                @if (evolutionPlan()) {
                  <app-evolution-plan [plan]="evolutionPlan()!" (back)="startNewCorrection()"></app-evolution-plan>
                }
              }
            }
          }
        </section>
      </div>

      <aside class="space-y-6 lg:col-span-2">
        <section class="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-card">
          <h3 class="text-base font-bold tracking-tight text-slate-900">Resumo da conta</h3>
          <dl class="mt-4 space-y-3 text-sm">
            <div class="flex justify-between gap-4">
              <dt class="text-slate-400">Plano atual</dt>
              <dd class="font-semibold text-slate-800">{{ getPlanLabel(userPlan()) }}</dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt class="text-slate-400">Status</dt>
              <dd class="font-semibold text-slate-800">{{ getSubscriptionStatusLabel(subscriptionSummary().status) }}</dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt class="text-slate-400">Vencimento</dt>
              <dd class="font-semibold text-slate-800">{{ getSubscriptionEndDateLabel(subscriptionSummary().endDate) }}</dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt class="text-slate-400">Créditos</dt>
              <dd class="font-semibold text-slate-800">@if (credits() > 900) { Ilimitado } @else { {{ credits() }} }</dd>
            </div>
          </dl>
          <button (click)="handleViewPlans()" class="btn-primary mt-5 w-full text-sm">
            Gerenciar assinatura
          </button>
        </section>

        @if (userPlan() === 'pro' && view() !== 'evolutionPlan') {
          <app-evolution-tracker
            [history]="correctionHistory()"
            (generatePlanRequest)="handleGeneratePlanRequest()">
          </app-evolution-tracker>
        }

        <section class="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-card">
          <h3 class="text-base font-bold tracking-tight text-slate-900">Histórico de redações</h3>
          <p class="mt-1 text-sm text-slate-400">Clique em uma redação para abrir o relatório.</p>
          <div class="mt-4 max-h-96 space-y-3 overflow-y-auto pr-1">
            @for (correction of allCorrectionsHistory(); track correction.id; let i = $index) {
              <button type="button" (click)="viewCorrection(correction)" class="group w-full rounded-xl border border-slate-200/60 bg-slate-50/50 p-3.5 text-left transition-all hover:border-blue-200 hover:bg-blue-50/40 hover:shadow-sm">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-semibold text-slate-700 group-hover:text-blue-700">Correção #{{ allCorrectionsHistory().length - i }}</p>
                    <p class="mt-0.5 text-xs text-slate-400">{{ correction.created_at | date:'dd/MM/yyyy HH:mm' }}</p>
                  </div>
                  <span class="rounded-lg px-2 py-1 text-lg font-bold" [class]="correction.data.notaFinal >= 6 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'">
                    {{ correction.data.notaFinal.toFixed(1) }}
                  </span>
                </div>
              </button>
            } @empty {
              <p class="py-6 text-center text-sm text-slate-500">Sem redações corrigidas por enquanto.</p>
            }
          </div>
        </section>
      </aside>
    </div>
  </div>
</div>
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, EssayEditorComponent, CorrectionReportComponent, EvolutionTrackerComponent, EvolutionPlanComponent],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private aiService = inject(AiService);
  private platformId = inject(PLATFORM_ID);
  private authService = inject(AuthService);
  private logService = inject(LogService);
  private supabase = inject(SupabaseService).client;

  readonly correctionResult = signal<CorrectionReportData | null>(null);
  readonly currentCorrectionId = signal<number | undefined>(undefined);
  readonly evolutionPlan = signal<EvolutionPlan | null>(null);
  readonly isLoading = signal(false);
  readonly isGeneratingPlan = signal(false);
  readonly view = signal<View>('editor');

  readonly currentUser = this.authService.currentUser;
  readonly credits = this.authService.effectiveCredits;
  readonly userPlan = computed(() => this.currentUser()?.plan ?? 'gratuito');

  readonly subscriptionSummary = signal<SubscriptionSummary>({
    status: 'none',
    planName: 'Sem assinatura ativa',
    endDate: null,
  });

  viewPlansRequest = output<void>();

  readonly correctionHistory = signal<Correction[]>([]);
  readonly allCorrectionsHistory = signal<CorrectionWithUser[]>([]);
  readonly reportHistory = computed(() => this.allCorrectionsHistory().map((c) => c.data));
  readonly preferredLayers = signal<Set<CorrectionLayer>>(new Set());
  readonly loadingStatus = signal<string>('Preparando análise da redação...');
  readonly loadingSteps = signal<string[]>([]);
  private loadingFeedbackTimer: ReturnType<typeof setInterval> | null = null;
  private readonly loadingFeedbackSequence = [
    { status: 'Lendo e organizando o texto...', step: 'Texto recebido e estruturado' },
    { status: 'Analisando gramática e ortografia...', step: 'Análise gramatical concluída' },
    { status: 'Avaliando coerência e argumentação...', step: 'Coerência e argumentação avaliadas' },
    { status: 'Calculando critérios e nota final...', step: 'Critérios e nota consolidados' },
    { status: 'Montando recomendações de melhoria...', step: 'Recomendações personalizadas geradas' },
  ];

  readonly isProTrialAvailable = computed(() => {
    const user = this.currentUser();
    if (!user) return false;
    return user.plan === 'gratuito' && !user.has_used_pro_trial && this.freePlanMonthlyCreditAvailable();
  });

  readonly freePlanHasPaidCredits = computed(() => {
    const user = this.currentUser();
    if (!user) return false;
    if (user.plan !== 'gratuito') return false;
    return Math.max(0, Number(user.credits ?? 0)) > 0;
  });

  readonly freePlanMonthlyCreditAvailable = computed(() => {
    const user = this.currentUser();
    if (!user) return false;
    if (user.plan !== 'gratuito') return false;
    if (!user.last_free_correction_at) return true;

    const lastDate = new Date(user.last_free_correction_at);
    if (Number.isNaN(lastDate.getTime())) return true;
    const now = new Date();
    return lastDate.getUTCMonth() !== now.getUTCMonth() || lastDate.getUTCFullYear() !== now.getUTCFullYear();
  });

  readonly hasFreeCredit = computed(() => this.freePlanHasPaidCredits() || this.freePlanMonthlyCreditAvailable());

  getPlanLabel(plan: UserPlan): string {
    switch (plan) {
      case 'basico':
        return 'Concurseiro Básico';
      case 'intermediario':
        return 'Concurseiro Intermediário';
      case 'pro':
        return 'Concurseiro Pro';
      default:
        return 'Gratuito';
    }
  }

  getSubscriptionStatusLabel(status: SubscriptionSummary['status']): string {
    switch (status) {
      case 'active':
        return 'Ativa';
      case 'pending':
        return 'Pendente';
      case 'canceled':
        return 'Cancelada';
      case 'expired':
        return 'Expirada';
      default:
        return 'Sem assinatura';
    }
  }

  getSubscriptionBadgeClass(status: SubscriptionSummary['status']): string {
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800';
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'canceled':
      case 'expired':
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  }

  getSubscriptionEndDateLabel(endDate: string | null): string {
    if (!endDate) return 'Sem vencimento definido';
    const date = new Date(endDate);
    if (Number.isNaN(date.getTime())) return 'Sem vencimento definido';
    return `Vence em ${date.toLocaleDateString('pt-BR')}`;
  }

  goToLatestReport() {
    if (this.correctionResult()) {
      this.view.set('report');
      return;
    }

    const latest = this.allCorrectionsHistory()[0];
    if (latest) {
      this.viewCorrection(latest);
    }
  }

  getCurrentCorrectionEssay(): string | null {
    const report = this.correctionResult() as (CorrectionReportData & { redacaoOriginal?: string }) | null;
    const essay = String(report?.redacaoOriginal ?? '').trim();
    return essay.length > 0 ? essay : null;
  }

  getCurrentCorrectionTheme(): string | null {
    const report = this.correctionResult() as (CorrectionReportData & { temaRedacao?: string }) | null;
    const theme = String(report?.temaRedacao ?? '').trim();
    return theme.length > 0 ? theme : null;
  }

  getMentorshipCurrentNoteLabel(): string {
    const latest = this.getLatestCorrectionData();
    if (!latest || typeof latest.notaFinal !== 'number' || Number.isNaN(latest.notaFinal)) {
      return 'Sem nota';
    }
    return latest.notaFinal.toFixed(1);
  }

  getMentorshipTargetNote(): number {
    const latest = this.getLatestCorrectionData();
    const current = latest?.notaFinal ?? 0;

    if (current >= 920) return 960;
    if (current >= 850) return 900;
    if (current >= 760) return 850;
    return 780;
  }

  getMentorshipTimelineLabel(): string {
    const latest = this.getLatestCorrectionData();
    if (!latest) return '4 a 6 semanas';

    const gap = Math.max(0, this.getMentorshipTargetNote() - (latest.notaFinal ?? 0));
    if (gap <= 25) return '2 a 3 semanas';
    if (gap <= 60) return '4 a 6 semanas';
    return '8 a 10 semanas';
  }

  getMentorshipBancaFocus(): string {
    const latest = this.getLatestCorrectionData() as (CorrectionReportData & { banca?: string }) | null;
    return latest?.banca ? latest.banca : 'Defina a banca na próxima redação';
  }

  getMentorshipWeakestCriterion(): string {
    const latest = this.getLatestCorrectionData() as (CorrectionReportData & {
      criterios?: Array<{ nome?: string; nota?: number }>;
      diagnosticoCognitivo?: Array<{ nome?: string; nota?: number }>;
    }) | null;
    if (!latest) return 'Sem diagnóstico ainda';

    const criterios = Array.isArray(latest.criterios) ? latest.criterios : [];
    if (criterios.length > 0) {
      const weakest = [...criterios].sort((a, b) => (a.nota ?? 0) - (b.nota ?? 0))[0];
      if (weakest?.nome) return weakest.nome;
    }

    const diagnosis = Array.isArray(latest.diagnosticoCognitivo) ? latest.diagnosticoCognitivo : [];
    if (diagnosis.length > 0) {
      const weakest = [...diagnosis].sort((a, b) => (a.nota ?? 0) - (b.nota ?? 0))[0];
      if (weakest?.nome) return weakest.nome;
    }

    return 'Coesão e progressão argumentativa';
  }

  getMentorshipNextAction(): string {
    const latest = this.getLatestCorrectionData() as (CorrectionReportData & {
      sugestoesMelhoria?: string[];
      pontosProblematicos?: Array<{ problema?: string }>;
      resumoEstrutural?: string[];
    }) | null;
    if (!latest) {
      return 'Envie sua primeira redação para receber um plano prático de 15 a 20 minutos.';
    }

    const suggestions = Array.isArray(latest.sugestoesMelhoria) ? latest.sugestoesMelhoria : [];
    if (suggestions.length > 0) {
      return this.truncateMentorshipText(suggestions[0], 160);
    }

    const problematic = Array.isArray(latest.pontosProblematicos) ? latest.pontosProblematicos : [];
    if (problematic.length > 0 && problematic[0]?.problema) {
      return this.truncateMentorshipText(`Foque em: ${problematic[0].problema}`, 160);
    }

    const structural = Array.isArray(latest.resumoEstrutural) ? latest.resumoEstrutural : [];
    if (structural.length > 0) {
      return this.truncateMentorshipText(structural[0], 160);
    }

    return 'Revise o desenvolvimento 2 com repertório válido e conectores de progressão argumentativa.';
  }

  private getLatestCorrectionData(): CorrectionReportData | null {
    const latest = this.allCorrectionsHistory()[0];
    return latest?.data ?? null;
  }

  private truncateMentorshipText(text: string, maxLength: number): string {
    const normalized = String(text ?? '').trim();
    if (!normalized) return '';
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, maxLength - 1).trimEnd()}...`;
  }

  private startLoadingFeedback() {
    this.stopLoadingFeedback();
    this.loadingStatus.set(this.loadingFeedbackSequence[0].status);
    this.loadingSteps.set([]);

    let index = 0;
    this.loadingFeedbackTimer = setInterval(() => {
      index = Math.min(index + 1, this.loadingFeedbackSequence.length - 1);
      const done = this.loadingFeedbackSequence.slice(0, index).map((item) => item.step);
      this.loadingSteps.set(done);
      this.loadingStatus.set(this.loadingFeedbackSequence[index].status);
    }, 2600);
  }

  private stopLoadingFeedback() {
    if (this.loadingFeedbackTimer) {
      clearInterval(this.loadingFeedbackTimer);
      this.loadingFeedbackTimer = null;
    }
    this.loadingStatus.set('Preparando análise da redação...');
    this.loadingSteps.set([]);
  }

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const storedLayers = localStorage.getItem(PREFERRED_LAYERS_KEY);
      if (storedLayers) {
        this.preferredLayers.set(new Set(JSON.parse(storedLayers)));
      }
    }

    await Promise.all([this.loadCorrectionHistory(), this.loadSubscriptionSummary()]);
  }

  ngOnDestroy(): void {
    this.stopLoadingFeedback();
  }

  private async loadSubscriptionSummary() {
    const user = this.currentUser();
    if (!user || user.id.startsWith('mock-user-id')) return;

    try {
      const { data, error } = await this.supabase
        .from('subscriptions')
        .select('status, end_date, plans(name)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !data) {
        return;
      }

      const rawStatus = (data as any).status as SubscriptionSummary['status'];
      const planName = ((data as any).plans?.name as string | undefined) ?? this.getPlanLabel(user.plan);
      const endDate = (data as any).end_date as string | null;

      this.subscriptionSummary.set({
        status: rawStatus ?? 'none',
        planName,
        endDate,
      });
    } catch {
    }
  }

  async loadCorrectionHistory() {
    const user = this.currentUser();
    if (!user || user.id.startsWith('mock-user-id')) {
      this.allCorrectionsHistory.set([]);
      this.correctionHistory.set([]);
      return;
    }

    try {
      const query = this.supabase
        .from('corrections')
        .select('id, data, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (user.plan === 'basico') {
        query.limit(10);
      }

      const { data, error } = await query;
      if (error) throw error;

      const allData: CorrectionWithUser[] = data.map(
        (item: any) =>
          ({
            id: item.id,
            data: item.data as CorrectionReportData,
            created_at: item.created_at,
          }) as CorrectionWithUser
      );

      this.allCorrectionsHistory.set(allData);
      this.correctionHistory.set(allData.map((c) => c.data).filter(isFullCorrection));
    } catch (error) {
      this.authService.showNotification('error', 'Erro no histórico', 'Não foi possível carregar seu histórico de correções.');
    }
  }

  async handleEssaySubmission(submission: { essay: string; banca: Banca; theme: string }) {
    const user = this.currentUser();
    if (!user) {
      this.authService.showNotification('error', 'Acesso negado', 'Você precisa estar logado para enviar uma redação.');
      return;
    }

    const normalizedTheme = String(submission.theme ?? '').trim();
    const normalizedEssay = String(submission.essay ?? '').trim();
    const allowedBancas: Banca[] = ['FCC', 'FGV', 'Cebraspe', 'ENEM'];
    const hasValidBanca = allowedBancas.includes(submission.banca);

    if (!normalizedTheme || !normalizedEssay || !hasValidBanca) {
      this.authService.showNotification(
        'error',
        'Dados incompletos',
        'Preencha tema, banca e texto da redação antes de enviar.'
      );
      return;
    }

    const plan = user.plan || 'gratuito';
    const isProTrial = this.isProTrialAvailable();

    this.isLoading.set(true);
    this.startLoadingFeedback();
    this.correctionResult.set(null);
    this.currentCorrectionId.set(undefined);

    const historicalDiagnoses = this.correctionHistory().map((c) => c.diagnosticoCognitivo);
    const fullEssayText = `TEMA: "${normalizedTheme}"\n\nREDAÇÃO:\n${normalizedEssay}`;

    try {
      const result = await this.aiService.correctEssay(fullEssayText, plan, isProTrial, historicalDiagnoses, submission.banca);
      const correctionWithEssay: CorrectionReportData = {
        ...result,
        banca: submission.banca,
        temaRedacao: normalizedTheme,
        redacaoOriginal: normalizedEssay,
      };
      await this.authService.refreshUserProfile().catch(() => undefined);

      const savedCorrectionId = await this.saveCorrection(correctionWithEssay, user.id);
      if (savedCorrectionId) {
        this.updateHistory(correctionWithEssay, savedCorrectionId);
        this.currentCorrectionId.set(savedCorrectionId);
      }

      this.loadingSteps.set(this.loadingFeedbackSequence.map((item) => item.step));
      this.loadingStatus.set('Análise concluída com sucesso.');
      this.correctionResult.set(correctionWithEssay);
      this.view.set('report');
      void this.logService.logInfo('Essay correction completed on dashboard', {
        domain: 'essay',
        event: 'essay.correction.dashboard_completed',
        status: 'success',
        userId: user.id,
        userPlan: plan,
        banca: submission.banca,
        correctionId: savedCorrectionId ?? null,
      });
      this.authService.showNotification('success', 'Correção concluída!', 'Sua redação foi analisada com sucesso.');

      if (isPlatformBrowser(this.platformId)) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    } catch (err: any) {
      const rawMessage = String(err?.message ?? '');
      const message = rawMessage.toLowerCase();
      const isAdminUser = Boolean(this.currentUser()?.isAdmin);
      const publicSupportMessage =
        'Não foi possível concluir sua correção agora. Tente novamente em instantes. Se o problema continuar, entre em contato com o suporte.';

      const isSessionError =
        message.includes('sessão expirada')
        || message.includes('invalid jwt')
        || message.includes('unauthorized');

      if (isSessionError) {
        this.authService.showNotification('info', 'Sessão expirada', 'Faça login novamente para continuar.');
        await this.authService.signOut();
        return;
      }

      const isBillingOrQuotaError =
        message.includes('insufficient_quota')
        || message.includes('exceeded your current quota')
        || message.includes('openai sem saldo')
        || message.includes('openai api error: 429');
      const isAccessOrCreditsError =
        message.includes('credito gratuito indisponivel')
        || message.includes('crédito gratuito indisponível')
        || message.includes('creditos insuficientes')
        || message.includes('créditos insuficientes')
        || message.includes('saldo mudou durante a correcao')
        || message.includes('saldo mudou durante a correção');
      const isPayloadError =
        message.includes('missing required correction payload')
        || message.includes('invalid request body')
        || message.includes('payload');

      await this.logService.logError('Dashboard essay submission failed', err, {
        isAdminUser,
        userId: user?.id ?? null,
        userPlan: plan,
        hasValidBanca,
        isSessionError,
        isBillingOrQuotaError,
        isAccessOrCreditsError,
        isPayloadError,
        rawMessage,
      });

      if (!isAdminUser) {
        if (isAccessOrCreditsError) {
          let userLimitMessage = 'Seu saldo de correções foi atualizado. Atualize a página e tente novamente.';
          if (message.includes('credito gratuito indisponivel') || message.includes('crédito gratuito indisponível')) {
            userLimitMessage = 'Seu crédito gratuito do mês já foi utilizado. Escolha um plano para continuar.';
          } else if (message.includes('creditos insuficientes') || message.includes('créditos insuficientes')) {
            userLimitMessage = 'Você não possui créditos suficientes para uma nova correção.';
          }

          this.authService.showNotification('error', 'Limite de uso', userLimitMessage);
          return;
        }

        if (isPayloadError) {
          this.authService.showNotification(
            'error',
            'Dados incompletos',
            'Não foi possível processar a redação. Revise os campos e tente novamente.'
          );
          return;
        }

        const publicTitle = isBillingOrQuotaError
          ? 'Serviço temporariamente indisponível'
          : 'Erro na correção';
        this.authService.showNotification('error', publicTitle, publicSupportMessage);
        return;
      }

      this.authService.showNotification('error', 'Erro na correção', rawMessage || 'Ocorreu um erro ao processar sua redação.');
    } finally {
      this.stopLoadingFeedback();
      this.isLoading.set(false);
    }
  }

  private async saveCorrection(correction: CorrectionReportData, userId: string): Promise<number | undefined> {
    if (userId.startsWith('mock-user-id')) {
      return Date.now();
    }

    const { data, error } = await this.supabase
      .from('corrections')
      .insert({ user_id: userId, data: correction as any })
      .select('id')
      .single();

    if (error) {
      await this.logService.logError('Dashboard failed to save correction', error, {
        userId,
      });
      this.authService.showNotification('error', 'Erro ao salvar', 'Não foi possível salvar esta correção no seu histórico.');
      return undefined;
    }
    void this.logService.logInfo('Essay correction saved to history', {
      domain: 'essay',
      event: 'essay.correction.saved',
      status: 'success',
      userId,
      correctionId: data.id,
    });
    return data.id;
  }

  private updateHistory(newCorrection: CorrectionReportData, newId: number) {
    const current = this.currentUser();
    if (!current) return;

    const newHistoryEntry: CorrectionWithUser = {
      id: newId,
      data: newCorrection,
      created_at: new Date().toISOString(),
      user_id: current.id,
      status: 'ok',
      report_reason: '',
      profiles: {
        name: current.name,
        email: current.email,
      },
    };

    this.allCorrectionsHistory.update((history) => [newHistoryEntry, ...history]);

    if (isFullCorrection(newCorrection)) {
      this.correctionHistory.update((history) => [newCorrection, ...history]);
    }
  }

  startNewCorrection() {
    this.correctionResult.set(null);
    this.evolutionPlan.set(null);
    this.currentCorrectionId.set(undefined);
    this.view.set('editor');
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  viewCorrection(correction: CorrectionWithUser) {
    this.correctionResult.set(correction.data);
    this.currentCorrectionId.set(correction.id);
    this.view.set('report');
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  handleVisibleLayersChange(layers: Set<CorrectionLayer>) {
    this.preferredLayers.set(layers);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(PREFERRED_LAYERS_KEY, JSON.stringify(Array.from(layers)));
    }
  }

  async handleGeneratePlanRequest() {
    const plan = this.userPlan();
    if (plan === 'gratuito' || plan === 'basico' || plan === 'intermediario') {
      this.authService.showNotification('info', 'Funcionalidade premium', 'O Plano de evolução está disponível apenas para assinantes Pro.');
      return;
    }

    if (this.correctionHistory().length < 3) {
      this.authService.showNotification('info', 'Dados insuficientes', 'É necessário ter pelo menos 3 redações corrigidas para gerar um plano.');
      return;
    }

    this.isGeneratingPlan.set(true);
    try {
      const planResult = await this.aiService.generateEvolutionPlan(this.correctionHistory());
      this.evolutionPlan.set(planResult);
      this.view.set('evolutionPlan');
      this.authService.showNotification('success', 'Plano gerado!', 'Sua mentoria de IA personalizada está pronta.');
    } catch (err) {
      await this.logService.logError('Dashboard evolution plan generation failed', err, {
        userId: this.currentUser()?.id ?? null,
        correctionHistorySize: this.correctionHistory().length,
      });
      this.authService.showNotification('error', 'Erro no plano', 'Ocorreu um erro ao gerar seu plano de evolução.');
      this.view.set('editor');
    } finally {
      this.isGeneratingPlan.set(false);
    }
  }

  handleViewPlans() {
    this.viewPlansRequest.emit();
  }
}
